import { useEffect, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  adminAnalyticsTimeseries, adminAnalyticsTopEarners, adminAnalyticsSourceBreakdown,
} from "@/lib/api";

const fmt = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Distinctive palette — chrome silver + electric blue + neon accents (matches brand)
const PIE_COLORS = ["#60a5fa", "#22c55e", "#a855f7", "#e2e8f0", "#f472b6", "#fbbf24", "#00F0FF", "#fb923c", "#94a3b8"];

const ChartCard = ({ title, testId, children, accent = "#60a5fa" }) => (
  <section data-testid={testId} className="border border-zinc-800 bg-zinc-950/60 p-5">
    <div className="mb-4 text-[10px] tracking-[0.3em] uppercase font-mono" style={{ color: accent }}>{title}</div>
    <div className="h-64 w-full">{children}</div>
  </section>
);

export const AdminCharts = () => {
  const [timeseries, setTimeseries] = useState(null);
  const [topEarners, setTopEarners] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [t, e, b] = await Promise.all([
          adminAnalyticsTimeseries(30),
          adminAnalyticsTopEarners(10),
          adminAnalyticsSourceBreakdown(),
        ]);
        if (cancelled) return;
        setTimeseries(t.series || []);
        setTopEarners(e.items || []);
        setBreakdown(b.items || []);
      } catch (ex) {
        if (cancelled) return;
        setErr(ex?.response?.data?.detail || ex?.message || "Failed to load analytics");
        setTimeseries([]);
        setTopEarners([]);
        setBreakdown([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) {
    return (
      <div data-testid="admin-charts-error" className="border border-red-800 bg-red-950/30 p-5 text-red-300 font-mono text-xs">
        // could not load analytics — {err}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-charts">
      {/* Revenue timeseries — full width */}
      <div className="lg:col-span-2">
        <ChartCard title="// revenue · last 30 days" accent="#22c55e" testId="chart-timeseries">
          {timeseries === null ? (
            <div className="text-zinc-300 font-mono text-xs">loading…</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeseries} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#1e293b" />
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} tick={{ fontFamily: "monospace" }} />
                <YAxis stroke="#94a3b8" fontSize={10} tickFormatter={(v) => `$${(v / 100).toFixed(0)}`} />
                <Tooltip
                  contentStyle={{ background: "#000", border: "1px solid #334155", fontFamily: "monospace", fontSize: 11 }}
                  labelStyle={{ color: "#e2e8f0" }}
                  formatter={(v, key) => (key === "jobs" ? [v, "jobs"] : [fmt(v), key])}
                />
                <Line type="monotone" dataKey="fees_cents" stroke="#22c55e" strokeWidth={2} dot={false} name="platform_fees" />
                <Line type="monotone" dataKey="gross_cents" stroke="#60a5fa" strokeWidth={2} dot={false} name="gross" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Top earners bar */}
      <ChartCard title="// top earners · lifetime" accent="#60a5fa" testId="chart-top-earners">
        {topEarners === null ? (
          <div className="text-zinc-300 font-mono text-xs">loading…</div>
        ) : topEarners.length === 0 ? (
          <div className="text-zinc-300 font-mono text-xs">no earnings yet</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topEarners.map((r) => ({ name: (r.email || "").split("@")[0].slice(0, 12), earned: r.earned_cents, jobs: r.jobs }))} margin={{ top: 5, right: 20, bottom: 30, left: 10 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#1e293b" />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={9} angle={-25} textAnchor="end" tick={{ fontFamily: "monospace" }} />
              <YAxis stroke="#94a3b8" fontSize={10} tickFormatter={(v) => `$${(v / 100).toFixed(0)}`} />
              <Tooltip
                contentStyle={{ background: "#000", border: "1px solid #334155", fontFamily: "monospace", fontSize: 11 }}
                formatter={(v, key) => (key === "jobs" ? [v, "jobs"] : [fmt(v), "earned"])}
              />
              <Bar dataKey="earned" fill="#60a5fa" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Source breakdown pie */}
      <ChartCard title="// revenue by category" accent="#a855f7" testId="chart-source-breakdown">
        {breakdown === null ? (
          <div className="text-zinc-300 font-mono text-xs">loading…</div>
        ) : breakdown.length === 0 ? (
          <div className="text-zinc-300 font-mono text-xs">no revenue yet</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={breakdown.map((r) => ({ name: r.category, value: r.gross_cents }))}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                stroke="#0b0b0b"
                strokeWidth={2}
              >
                {breakdown.map((_r, i) => (
                  <Cell key={`c-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: "#000", border: "1px solid #334155", fontFamily: "monospace", fontSize: 11 }}
                formatter={(v) => fmt(v)}
              />
              <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 10, color: "#e2e8f0" }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
};
